import React, { useCallback, useId, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  ChevronDown,
  Flame,
  Gamepad2,
  Gift,
  Goal,
  Lightbulb,
  MessageCircle,
  Play,
  Rocket,
  Scale,
  ShieldAlert,
  Smartphone,
  Sparkles,
  Target,
  TrendingUp,
  Wallet,
  Zap,
} from "lucide-react";
import {
  BETTING_GAMING_BONUS_TOPICS,
  BETTING_GAMING_DEMAND_INTRO,
  BETTING_GAMING_DEMAND_TOPICS,
  BETTING_GAMING_PRIORITY_LINKS,
  BETTING_GAMING_RESPONSIBLE,
  BETTING_GAMING_STRATEGIC,
} from "../../data/bettingGamingCategoryPageContent";

const TOPIC_ICONS = [Goal, Rocket, Smartphone, Sparkles, Gift, BarChart3, Scale, Play, Gamepad2, Wallet];

function PriorityChip({ to, label }) {
  return (
    <Link
      to={to}
      className="group relative flex shrink-0 snap-start scroll-ml-1 items-center gap-2 overflow-hidden rounded-full border border-white/15 bg-white/10 py-2.5 pl-4 pr-3 text-[13px] font-bold text-white/95 backdrop-blur-md transition first:scroll-ml-0 hover:border-[#FFD221]/50 hover:bg-white/15"
    >
      <span className="absolute inset-0 bg-gradient-to-r from-[#FFD221]/0 via-[#FFD221]/12 to-[#FFD221]/0 opacity-0 transition group-hover:opacity-100" />
      <span className="relative">{label}</span>
      <ArrowRight className="relative h-3.5 w-3.5 text-[#FFD221] transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function DemandTopicCardFeatured({ topic, base, Icon, index }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden rounded-[1.75rem] border border-orange-400/25 bg-gradient-to-br from-[#1c1410] via-[#15100e] to-[#0c0a09] p-1 shadow-[0_40px_100px_-40px_rgba(249,115,22,0.45)] sm:rounded-[2rem]"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#FFD221]/10 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative grid gap-8 p-6 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-10 sm:p-8 lg:p-10">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#FFD221]/35 bg-[#FFD221]/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
              <TrendingUp className="h-3 w-3" strokeWidth={2.5} />
              #1 demand signal
            </span>
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-orange-300 ring-1 ring-white/10">
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
          </div>
          <div className="mt-5 flex items-end gap-4">
            <span className="font-black leading-none text-[4.5rem] tracking-tighter text-white/10 sm:text-[5.5rem]">
              {String(topic.rank).padStart(2, "0")}
            </span>
          </div>
          <h3 className="-mt-8 max-w-xl text-2xl font-black leading-[1.1] tracking-tight text-white sm:text-3xl lg:text-4xl">
            {topic.title}
          </h3>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-orange-100/80 sm:text-base">
            {topic.why}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`${base}/${topic.slug}`}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#FFD221] px-6 py-3.5 text-sm font-black text-black shadow-lg shadow-orange-900/40 transition hover:brightness-105 active:scale-[0.99]"
            >
              Explore hub
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
            <span className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-xs font-bold text-white/70">
              {topic.questions.length} high-intent questions · {topic.contentIdeas.length} content angles
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:w-[min(100%,20rem)] sm:shrink-0">
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm">
            <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-[#FFD221]/90">
              <MessageCircle className="h-3.5 w-3.5" />
              Top searches
            </p>
            <ul className="mt-3 space-y-2">
                  {topic.questions.slice(0, 4).map((q) => (
                    <li key={q}>
                      <Link
                        to={`${base}/${topic.slug}`}
                        className="line-clamp-2 text-[13px] font-semibold leading-snug text-white hover:text-[#FFD221]"
                      >
                        {q}
                      </Link>
                    </li>
                  ))}
            </ul>
          </div>
          <div className="flex flex-wrap gap-2">
            {topic.contentIdeas.map((idea) => (
              <span
                key={idea}
                className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1 text-[11px] font-semibold text-orange-100/85"
              >
                {idea}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function DemandTopicCardHighlight({ topic, base, Icon, rankAccent, index }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`relative flex flex-col overflow-hidden rounded-2xl border bg-white p-5 shadow-[0_20px_50px_-28px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.04] transition-shadow hover:shadow-[0_28px_60px_-24px_rgba(249,115,22,0.18)] sm:p-6 ${rankAccent}`}
    >
      <div className="pointer-events-none absolute -right-8 top-0 h-32 w-32 rounded-full bg-gradient-to-br from-orange-400/15 to-transparent blur-2xl" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-lg font-black text-[#FFD221] shadow-inner">
            {topic.rank}
          </span>
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600 ring-1 ring-orange-100">
            <Icon className="h-5 w-5" strokeWidth={2} />
          </span>
        </div>
        <Link
          to={`${base}/${topic.slug}`}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-900 transition hover:border-orange-300 hover:text-orange-600"
          aria-label={`Open ${topic.title} hub`}
        >
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <h3 className="relative mt-4 text-lg font-black leading-snug text-gray-950 sm:text-xl">
        {topic.title}
      </h3>
      <p className="relative mt-2 flex-1 text-sm leading-relaxed text-gray-600">{topic.why}</p>
      <Link
        to={`${base}/${topic.slug}`}
        className="relative mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 transition hover:gap-2"
      >
        View topic hub
        <ArrowRight className="h-4 w-4" />
      </Link>
    </motion.article>
  );
}

function DemandTopicCardCompact({ topic, base, Icon, isOpen, onToggle, index }) {
  const contentId = useId();

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: index * 0.035 }}
      className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white/90 shadow-sm ring-1 ring-black/[0.03] backdrop-blur-sm transition hover:border-orange-200/90 hover:shadow-md"
    >
      <button
        type="button"
        id={`${contentId}-trigger`}
        aria-expanded={isOpen}
        aria-controls={`${contentId}-panel`}
        onClick={onToggle}
        className="flex w-full items-start gap-4 p-4 text-left transition hover:bg-orange-50/30 sm:p-5"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-stone-100 to-white text-sm font-black text-gray-900 ring-1 ring-gray-200/80">
          {topic.rank}
        </span>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="text-base font-black leading-snug text-gray-950 sm:text-[1.0625rem]">{topic.title}</h3>
          <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-600">{topic.why}</p>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            key="panel"
            id={`${contentId}-panel`}
            role="region"
            aria-labelledby={`${contentId}-trigger`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-orange-100/80 bg-gradient-to-b from-orange-50/20 to-white"
          >
            <div className="grid gap-6 p-4 pt-5 sm:grid-cols-2 sm:p-6">
              <div>
                <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">
                  <MessageCircle className="h-3.5 w-3.5 text-orange-500" />
                  Most asked
                </p>
                <ul className="mt-3 space-y-2">
                  {topic.questions.map((q) => (
                    <li key={q}>
                      <Link
                        to={`${base}/${topic.slug}`}
                        className="text-sm font-semibold text-gray-800 underline-offset-2 transition hover:text-orange-600 hover:underline"
                      >
                        {q}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">
                  <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
                  Content ideas
                </p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {topic.contentIdeas.map((idea) => (
                    <li key={idea}>
                      <span className="inline-block rounded-lg border border-amber-100/90 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700 shadow-sm">
                        {idea}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="border-t border-orange-100/60 px-4 pb-4 sm:px-6 sm:pb-5">
              <Link
                to={`${base}/${topic.slug}`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-950 py-3 text-sm font-black text-[#FFD221] transition hover:bg-gray-800 sm:w-auto sm:px-6"
              >
                Open full hub
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.article>
  );
}

export default function BettingGamingCategoryPageSections({ industryId }) {
  const base = `/${industryId}`;
  const [openSlug, setOpenSlug] = useState(null);

  const toggleSlug = useCallback((slug) => {
    setOpenSlug((prev) => (prev === slug ? null : slug));
  }, []);

  const [first, second, third, ...rest] = BETTING_GAMING_DEMAND_TOPICS;

  return (
    <section className="relative z-10 px-4 pb-10 pt-2 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[min(50vh,28rem)] bg-gradient-to-b from-orange-200/25 via-transparent to-transparent blur-3xl" />

      <div className="mx-auto max-w-8xl overflow-hidden rounded-[2rem] border border-gray-200/80 bg-white shadow-[0_50px_100px_-48px_rgba(0,0,0,0.28)] sm:rounded-[2.5rem]">
        {/* —— Hero rail —— */}
        <div className="relative overflow-hidden px-5 pb-10 pt-9 sm:px-8 sm:pb-12 sm:pt-11 lg:px-12">
          <div className="absolute inset-0 bg-gradient-to-br from-[#14110f] via-[#1a1612] to-[#0d0b0a]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_70%_-10%,rgba(249,115,22,0.22),transparent_55%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_0%_100%,rgba(255,210,33,0.08),transparent_50%)]" />

          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="inline-flex items-center gap-2 rounded-full border border-[#FFD221]/25 bg-[#FFD221]/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#FFD221]"
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                {BETTING_GAMING_DEMAND_INTRO.kicker}
                <span className="hidden text-[#FFD221]/50 sm:inline">·</span>
                <span className="hidden text-[#FFD221]/80 sm:inline">10 topics</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.06 }}
                className="mt-5 text-3xl font-black leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]"
              >
                {BETTING_GAMING_DEMAND_INTRO.title}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-4 max-w-2xl text-sm leading-relaxed text-orange-100/75 sm:text-base"
              >
                {BETTING_GAMING_DEMAND_INTRO.subtitle}
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="relative shrink-0 rounded-2xl border border-white/10 bg-white/[0.07] p-4 backdrop-blur-md lg:max-w-md"
            >
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FFD221]/90">
                Start here · highest engagement
              </p>
              <div className="mt-3 flex snap-x gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden">
                {BETTING_GAMING_PRIORITY_LINKS.map(({ label, slug }) => (
                  <PriorityChip key={slug + label} to={`${base}/${slug}`} label={label} />
                ))}
              </div>
            </motion.div>
          </div>

          <div className="relative mt-10 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/35">
            <Zap className="h-4 w-4 text-[#FFD221]/80" />
            Scroll the list · expand any row for questions & angles
            <span className="hidden h-px flex-1 bg-gradient-to-r from-white/15 to-transparent sm:block" />
          </div>
        </div>

        {/* —— Bento body —— */}
        <div className="relative space-y-6 bg-gradient-to-b from-stone-100/95 via-white to-white px-4 py-8 sm:px-8 lg:space-y-8 lg:px-10 lg:py-10">
          <LayoutGroup>
            <DemandTopicCardFeatured
              topic={first}
              base={base}
              Icon={TOPIC_ICONS[0]}
              index={0}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <DemandTopicCardHighlight
                topic={second}
                base={base}
                Icon={TOPIC_ICONS[1]}
                rankAccent="border-orange-200/90"
                index={1}
              />
              <DemandTopicCardHighlight
                topic={third}
                base={base}
                Icon={TOPIC_ICONS[2]}
                rankAccent="border-amber-200/80"
                index={2}
              />
            </div>

            <div className="space-y-3">
              {rest.map((topic, i) => {
                const Icon = TOPIC_ICONS[topic.rank - 1] ?? Sparkles;
                return (
                  <DemandTopicCardCompact
                    key={topic.slug}
                    topic={topic}
                    base={base}
                    Icon={Icon}
                    isOpen={openSlug === topic.slug}
                    onToggle={() => toggleSlug(topic.slug)}
                    index={i + 3}
                  />
                );
              })}
            </div>
          </LayoutGroup>

          {/* Bonus */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-2xl border border-orange-200/60 bg-gradient-to-br from-orange-50/90 via-white to-amber-50/40 p-6 shadow-inner sm:p-8"
          >
            <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 translate-x-1/3 -translate-y-1/3 rounded-full bg-orange-400/10 blur-3xl" />
            <p className="relative flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-orange-700">
              <Flame className="h-4 w-4" />
              Bonus high-demand angles
            </p>
            <p className="relative mt-2 max-w-2xl text-sm text-gray-600">
              Extra search lanes that pair with the top ten — each link lands in the closest hub.
            </p>
            <div className="relative mt-5 flex flex-wrap gap-2">
              {BETTING_GAMING_BONUS_TOPICS.map(({ label, slug }) => (
                <Link
                  key={label}
                  to={`${base}/${slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-orange-200/80 bg-white px-4 py-2 text-xs font-bold text-gray-800 shadow-sm transition hover:border-orange-400 hover:shadow-md"
                >
                  {label}
                  <ArrowRight className="h-3 w-3 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Strategic */}
          <div className="grid gap-px overflow-hidden rounded-2xl bg-gray-200/80 ring-1 ring-gray-200/80 sm:grid-cols-3">
            <div className="bg-white p-6 sm:col-span-1 sm:p-8">
              <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">
                <Target className="h-4 w-4" />
                Strategic fit
              </p>
              <h3 className="mt-3 text-lg font-black leading-snug text-gray-950">{BETTING_GAMING_STRATEGIC.title}</h3>
            </div>
            <div className="bg-gradient-to-br from-white to-slate-50/80 p-6 sm:col-span-2 sm:grid sm:grid-cols-3 sm:gap-6 sm:p-8">
              {[
                { k: "Intent", v: BETTING_GAMING_STRATEGIC.intent },
                { k: "Education", v: BETTING_GAMING_STRATEGIC.education },
                { k: "Conversion", v: BETTING_GAMING_STRATEGIC.conversion },
              ].map(({ k, v }) => (
                <div key={k}>
                  <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">{k}</p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-700">{v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Responsible */}
          <div className="flex flex-col gap-4 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-amber-50/30 p-5 sm:flex-row sm:items-start sm:gap-6 sm:p-7">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-900 shadow-sm ring-1 ring-amber-200/80">
              <ShieldAlert className="h-6 w-6" strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-amber-900">18+ · Play responsibly</p>
              <h3 className="mt-1 text-xl font-black text-gray-950">{BETTING_GAMING_RESPONSIBLE.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-700">{BETTING_GAMING_RESPONSIBLE.body}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
